/**
 * One document: what was invoiced, what has been paid against it, and the
 * file the customer received.
 *
 * The page states the same figures three ways on purpose — the totals block,
 * the payments table and the outstanding line — because this is the screen
 * somebody opens with a customer on the telephone disputing one of them. Each
 * comes from the server: nothing here recomputes a total from the lines, and
 * the outstanding figure is the document's own signed one.
 *
 * The provenance line is not decoration. A document received from a
 * connector cannot be corrected here, and knowing that before trying is the
 * difference between a phone call to the accounts department and ten minutes
 * spent looking for an edit button that should not exist.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { download, useAction, useResource } from "@hardauto/core-data";
import { day, money, percent, quantity, EM_DASH } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  useToast,
  type Column,
  type Definition,
} from "@hardauto/core-ui";

import { BillingApi, type DocumentLine, type Settlement } from "../api";
import { SettleDialog } from "../SettleDialog";
import { NAMESPACE } from "../i18n";
import { BILLING_PATHS } from "../paths";
import { STATUS_TONE } from "../status";
import "./billing.css";

export function DocumentScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new BillingApi(client), [client]);
  const action = useAction();
  const toast = useToast();
  const maySettle = useCan("billing.settlement.write");

  const [settling, setSettling] = useState(false);

  const fetcher = useCallback(() => api.get(id), [api, id]);
  const detail = useResource(fetcher);

  const lineColumns: Column<DocumentLine>[] = [
    { key: "reference", header: t("document.lines.reference"), render: (row) => row.reference },
    {
      key: "designation",
      header: t("document.lines.designation"),
      render: (row) => row.designation,
    },
    {
      key: "quantity",
      header: t("document.lines.quantity"),
      align: "end",
      render: (row) => (
        <span className="hardauto-num">
          {quantity(row.quantity, locale)} {row.sales_unit}
        </span>
      ),
    },
    {
      key: "unitPrice",
      header: t("document.lines.unitPrice"),
      align: "end",
      render: (row) => <span className="hardauto-num">{money(row.unit_price, locale)}</span>,
    },
    {
      key: "discount",
      header: t("document.lines.discount"),
      align: "end",
      secondary: true,
      render: (row) => (Number(row.discount_pct) > 0 ? percent(row.discount_pct, locale) : EM_DASH),
    },
    {
      key: "amount",
      header: t("document.lines.amount"),
      align: "end",
      render: (row) => (
        <span className="hardauto-num hardauto-num--strong">{money(row.amount, locale)}</span>
      ),
    },
  ];

  const settlementColumns: Column<Settlement>[] = [
    { key: "date", header: t("column.date"), render: (row) => day(row.received_on, locale) },
    {
      key: "amount",
      header: t("column.amount"),
      align: "end",
      render: (row) => <span className="hardauto-num">{money(row.amount, locale)}</span>,
    },
    { key: "method", header: t("settle.method"), render: (row) => t(`method.${row.method}`) },
    {
      key: "reference",
      header: t("document.settlements.reference"),
      render: (row) => row.reference || EM_DASH,
    },
  ];

  if (detail.loading) return <LoadingState label={t("document.loading")} />;
  if (detail.failed && !detail.data) {
    return (
      <ErrorState
        title={t("error.title")}
        description={t("error.body")}
        retryLabel={t("error.retry")}
        onRetry={detail.reload}
      />
    );
  }
  if (!detail.data) {
    return (
      <EmptyState title={t("document.notFound.title")} description={t("document.notFound.body")} />
    );
  }

  const { document, settlements } = detail.data;
  const customer = `${detail.data.client_name} (${detail.data.client_code})`;

  const meta: Definition[] = [
    {
      // The provenance decides what can be done with this document at all: one
      // a connector delivered is corrected in the ERP, not here. Inside the
      // list rather than above it, so it reads as a fact about the document
      // instead of a heading for the ones below.
      label: t("document.meta.origin"),
      value: detail.data.external_ref
        ? t("document.meta.originExternal", { reference: detail.data.external_ref })
        : t("document.meta.originLocal"),
    },
    {
      label: t("document.meta.client"),
      value: (
        <Link to={BILLING_PATHS.statement.replace("$id", detail.data.client_id)}>{customer}</Link>
      ),
    },
    { label: t("document.meta.issued"), value: day(document.issued_on, locale) },
    { label: t("document.meta.due"), value: day(document.due_on, locale) },
    {
      label: t("document.meta.order"),
      value: document.order_reference || EM_DASH,
    },
    { label: t("document.meta.site"), value: document.site_code || EM_DASH },
    { label: t("document.meta.currency"), value: document.currency },
  ];

  const totals: Definition[] = [
    { label: t("document.totals.subtotal"), value: money(document.subtotal, locale) },
    { label: t("document.totals.tax"), value: money(document.tax_amount, locale) },
    { label: t("document.totals.total"), value: money(document.total, locale) },
    { label: t("document.totals.paid"), value: money(document.paid_amount, locale) },
    { label: t("document.totals.outstanding"), value: money(document.outstanding, locale) },
  ];

  return (
    <Page
      title={document.number}
      eyebrow={
        <>
          {t(`kind.${document.kind}`)} ·{" "}
          <Badge tone={STATUS_TONE[document.status]}>{t(`status.${document.status}`)}</Badge>
        </>
      }
      back={<Link to={BILLING_PATHS.ledger}>{t("document.back")}</Link>}
      actions={
        <>
          {maySettle && Number(document.outstanding) > 0 ? (
            <Button onClick={() => setSettling(true)}>{t("settle.open")}</Button>
          ) : null}
          <Button
            variant="primary"
            busy={action.isBusy("pdf")}
            busyLabel={t("document.pdf.downloading")}
            onClick={() =>
              void action.run("pdf", async () => {
                try {
                  download(await api.pdf(document.id), `${document.number}.pdf`);
                } catch {
                  toast.show(t("document.pdf.failed"), { tone: "danger" });
                }
              })
            }
          >
            {t("document.pdf.download")}
          </Button>
        </>
      }
    >
      <div className="hardauto-stack">
        {/* The two short lists side by side, the lines below them at full
          width. The lines went in the left half first, and six columns in
          half a page put the amount — the one figure somebody opens this
          screen to check — behind a horizontal scrollbar. */}
        <div className="hardauto-billing__split">
          <Card title={t("document.meta.title")} compactTitle>
            <DefinitionList items={meta} layout="inline" />
          </Card>
          <Card title={t("document.totals.title")} compactTitle>
            <DefinitionList items={totals} layout="inline" />
          </Card>
        </div>

        <Card title={t("document.lines.title")} flush>
          {document.lines.length === 0 ? (
            <div className="hardauto-billing__padded">
              <EmptyState title={t("document.lines.empty")} />
            </div>
          ) : (
            <DataTable<DocumentLine>
              caption={t("document.lines.caption")}
              columns={lineColumns}
              rows={document.lines}
              rowKey={(row) => `${row.position}-${row.reference}`}
              empty={{ title: t("document.lines.empty") }}
              loadingLabel={t("document.loading")}
            />
          )}
        </Card>

        <Card title={t("document.settlements.title")} flush>
          <DataTable<Settlement>
            caption={t("document.settlements.caption")}
            columns={settlementColumns}
            rows={settlements}
            rowKey={(row) => row.id}
            empty={{ title: t("document.settlements.empty") }}
            loadingLabel={t("document.loading")}
          />
        </Card>
      </div>

      {maySettle ? (
        <SettleDialog
          api={api}
          open={settling}
          onClose={() => setSettling(false)}
          clientId={detail.data.client_id}
          clientLabel={customer}
          documents={[document]}
          documentId={document.id}
          onRecorded={detail.reload}
        />
      ) : null}
    </Page>
  );
}
